<script lang="ts">
  import type { MarqueeSectionProps } from './types';
  import { onMount, onDestroy } from 'svelte';

  type $$Props = MarqueeSectionProps;

  export let entryId: MarqueeSectionProps['entryId'];
  export let ImageAttributes: MarqueeSectionProps['ImageAttributes'];
  export let scrollEnabled: MarqueeSectionProps['scrollEnabled'] = undefined;
  let marqueeStrip: HTMLParagraphElement;
  let section: HTMLElement;
  let observer: IntersectionObserver;
  let animationEnabled = false;
  let containerWidth: number;
  const IMAGE_FORMATS = ['avif', 'webp'];
  const scrollSpeed = 140; // unit is pixel per second

  const disableLazyLoading = () => {
    section.querySelectorAll('img').forEach((img) => {
      img.setAttribute('loading', 'eager');
    });
  };

  onMount(() => {
    if (scrollEnabled) {
      animationEnabled = true;
    }

    // disable lazy loading when it comes in view
    observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        disableLazyLoading();
        observer.unobserve(section);
      }
    });
    observer.observe(section);
  });

  onDestroy(() => {
    observer?.disconnect();
  });

  $: duration = containerWidth / scrollSpeed;
  $: _marqueeStyle = `
    --play: ${animationEnabled ? 'running' : 'paused'};
    --duration: ${duration}s;
    `;
</script>

<div data-entry-id={entryId} class="MarqueeSection MarqueeSection-{entryId}-box-properties" bind:this={section}>
  <div class="content">
    <div class="headings-compact {scrollEnabled ? 'fade-at-wider-screen' : ''}">
      <p class="scroll" style={_marqueeStyle} bind:this={marqueeStrip} bind:clientWidth={containerWidth}>
        {#each ImageAttributes as image}
          <picture>
            {#each IMAGE_FORMATS as format}
              <source srcset={`${image.src}?fm=${format}`} type="image/{format}" />
            {/each}
            <img loading="lazy" alt={image.alt} src={image.src} />
          </picture>
        {/each}
        <!-- when scrollEnabled = true, duplicate the images to make the animation more seamless -->
        {#if animationEnabled}
          {#each ImageAttributes as image}
            <picture>
              {#each IMAGE_FORMATS as format}
                <source srcset={`${image.src}?fm=${format}`} type="image/{format}" />
              {/each}
              <img loading="lazy" alt={image.alt} src={image.src} />
            </picture>
          {/each}
        {/if}
      </p>
    </div>
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/media_queries' as mediaQueries;

  .MarqueeSection {
    --logo-height: 55px;
    --gap: var(--space-60);
    --scroll-start: -10%;
    --scroll-end: calc(-60% - calc(var(--gap) / 2));

    @keyframes scroll-x {
      from {
        transform: translateX(var(--scroll-start));
      }
      to {
        transform: translateX(var(--scroll-end));
      }
    }

    .headings-compact {
      max-width: none;
      display: flex;
      user-select: none;
    }

    :global(.headings-compact p) {
      max-width: none;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: space-around;
      min-width: 100%;
      margin: 0;
      gap: var(--gap);
      transition: opacity var(--duration-default);
    }

    :global(.headings-compact p.scroll) {
      animation: scroll-x var(--duration) linear infinite;
      animation-play-state: var(--play);
    }

    :global(.headings-compact p img) {
      place-items: center;
      padding: 0;
      height: var(--logo-height);
    }

    @include mediaQueries.at('small') {
      --gap: var(--space-80);
    }

    @media (min-width: 3000px) {
      .headings-compact {
        &.fade-at-wider-screen {
          overflow: hidden;
          position: relative;

          &:before,
          &:after {
            content: ' ';
            display: block;
            position: absolute;
            width: 100px;
            height: 200px;
            transition: translateY(-50%);
            z-index: 2;
          }
          &:before {
            background: linear-gradient(270deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 70%);
            left: 0;
            top: 0;
          }
          &:after {
            background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 70%);
            right: 0;
            top: 0;
          }
        }
      }
    }
  }
</style>
